import React from "react";
import { createPortal } from "react-dom";
import { Spinner } from "reactstrap";

/**
 * Overlay component that shows during auto capture process
 */
const AutoCaptureOverlay = ({
  isVisible,
  progress,
  total,
  status,
  onClose,
  onDownload,
  error,
  currentRoomName = "",
  completedRooms = 0,
  totalRooms = 0,
  checklistItems = [],
  completedItems = [],
}) => {
  if (!isVisible) return null;

  const progressPercent = total > 0 ? (progress / total) * 100 : 0;
  const completedItemsSet = new Set(completedItems); // Convert array to Set for efficient lookup

  const overlayContent = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        margin: 0,
        padding: 0,
        backgroundColor: "white",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "stretch",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {status === "failed" && (
        <div
          style={{
            backgroundColor: "#dc3545",
            color: "white",
            padding: "1rem",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          Something went wrong. Please try again.
        </div>
      )}

      {error && (
        <div
          style={{
            backgroundColor: "#dc3545",
            color: "white",
            padding: "1rem",
            textAlign: "center",
            fontWeight: "500",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        {status === "processing" && (
          <>
            <div className="d-flex align-items-center justify-content-center mb-4">
              <Spinner color="primary" className="me-3" style={{ width: "3rem", height: "3rem" }} />
              <h3 className="mb-0" style={{ fontSize: "2rem" }}>Capturing Images...</h3>
            </div>
            {currentRoomName && (
              <div className="mb-3" style={{ fontSize: "1.1rem", color: "#666" }}>
                <i className="fa-solid fa-door-open me-2" />
                Current Room: <strong>{currentRoomName}</strong>
              </div>
            )}
            {/* Checklist of all rooms and views */}
            {checklistItems.length > 0 && (
              <div style={{ width: "100%", maxWidth: "600px", marginBottom: "2rem" }}>
                <div className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "600", color: "#333" }}>
                  Progress Checklist
                </div>
                <div
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "1rem",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {checklistItems.map((item) => {
                    const isCompleted = completedItemsSet.has(item.id);
                    return (
                      <div
                        key={item.id}
                        className="d-flex align-items-center mb-2"
                        style={{
                          padding: "0.5rem",
                          borderRadius: "4px",
                          backgroundColor: isCompleted ? "#e8f5e9" : "transparent",
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <div className="me-3" style={{ minWidth: "24px", textAlign: "center" }}>
                          {isCompleted ? (
                            <i className="fa-solid fa-check-circle text-success" style={{ fontSize: "1.2rem" }} />
                          ) : (
                            <i className="fa-regular fa-circle text-muted" style={{ fontSize: "1.2rem", opacity: 0.5 }} />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "1rem",
                            color: isCompleted ? "#2e7d32" : "#666",
                            fontWeight: isCompleted ? "500" : "400",
                          }}
                        >
                          {item.roomName} - {item.imageTypeDisplay}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {status === "completed" && (
          <>
            <div className="d-flex align-items-center justify-content-center mb-4">
              <i
                className="fa-solid fa-check-circle text-success"
                style={{ fontSize: "5rem" }}
              />
            </div>
            <h3 className="text-center mb-4" style={{ fontSize: "2rem" }}>
              Capture Completed!
            </h3>
            <p className="text-center text-muted" style={{ fontSize: "1rem" }}>
              Closing automatically...
            </p>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="d-flex align-items-center justify-content-center mb-4">
              <i
                className="fa-solid fa-exclamation-circle text-danger"
                style={{ fontSize: "5rem" }}
              />
            </div>
            <h3 className="text-center mb-4" style={{ fontSize: "2rem" }}>
              Capture Failed
            </h3>
            <div className="d-flex gap-2 justify-content-center">
              <button
                className="btn btn-dark"
                onClick={onClose}
                style={{
                  padding: "0.75rem 2rem",
                  fontSize: "1.1rem",
                }}
              >
                <i className="fa-solid fa-times me-2" />
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Use React Portal to render directly to document.body
  // This ensures the overlay is not constrained by any parent containers
  return createPortal(overlayContent, document.body);
};

export default AutoCaptureOverlay;

